// src/pages/admin/components/NudgeCard.jsx
//
// Founder-only nudge action card for /admin/users/:uid. One recovery email
// per user, EVER — the server (adminSendOnboardingNudge) is the enforcement
// boundary; everything here is UX. Client-side we pre-disable for the states
// we can see (already nudged, too new, activated, no stage) and surface the
// server's refusal reasons for the ones we can't (suppression, live-stage
// ineligibility). See features/onboarding-recovery/ (AC4-AC8, UAT step 6).

import React, { useState } from 'react';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { PaperAirplaneIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const NUDGE_MIN_AGE_MS = 72 * 60 * 60 * 1000;

function toMs(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.toDate === 'function') return ts.toDate().getTime();
  if (ts.seconds) return ts.seconds * 1000;
  return Number(ts) || 0;
}

// Server refusal codes → operator-readable reasons (UAT step 6 expects
// four distinct messages).
function reasonText(message) {
  if (!message) return 'Send failed.';
  if (message === 'already_nudged') return 'This user has already received their one nudge.';
  if (message.startsWith('too_new')) {
    const hours = message.split(':')[1];
    return `Too soon — signed up under 72h ago (${hours || '?'}h to go).`;
  }
  if (message === 'suppressed') return 'This address is on the suppression list. Never email it.';
  if (message.startsWith('not_eligible')) return 'User is past the nudge stages (active or activated).';
  if (message === 'suppression_check_failed') return 'Suppression check unavailable — send refused (fails closed). Try later.';
  if (message === 'no_email') return 'No email address on the user doc.';
  return message;
}

export default function NudgeCard({ uid, user, onSent }) {
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const nudge = user?.onboardingNudge;
  const onboarding = user?.onboarding;
  const ageMs = Date.now() - toMs(user?.createdAt);
  const tooNew = !toMs(user?.createdAt) || ageMs < NUDGE_MIN_AGE_MS;
  const hoursLeft = Math.max(0, Math.ceil((NUDGE_MIN_AGE_MS - ageMs) / 3600000));
  const activated = !!onboarding?.activated;
  const stage = onboarding?.stage;
  const stageEligible = stage >= 3 && stage <= 6 && !activated;

  // Pre-disabled states, cheapest first.
  let disabledReason = null;
  if (nudge) disabledReason = `Nudge already sent (stage ${nudge.stage}, ${nudge.templateId}). One per user, ever.`;
  else if (activated) disabledReason = 'Activated — nothing to recover.';
  else if (tooNew) disabledReason = `Signed up under 72h ago. Eligible in ~${hoursLeft}h.`;
  else if (onboarding && !stageEligible) disabledReason = 'Outside nudge stages (3-6).';

  async function send() {
    setSending(true);
    setError(null);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'adminSendOnboardingNudge');
      const res = await fn({ uid });
      setResult(res.data);
      setConfirming(false);
      onSent?.(res.data);
    } catch (e) {
      setError(reasonText(e?.message));
      setConfirming(false);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5">
      <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-500 mb-2">Recovery nudge</h2>

      {result ? (
        <div className="flex items-start gap-2 text-sm text-lime-800 bg-lime-50 border border-lime-200 rounded-md px-3 py-2">
          <CheckCircleIcon className="h-5 w-5 shrink-0" />
          <p>
            Sent — stage {result.sentStage} template ({result.templateId}), {result.lang === 'es' ? 'Spanish' : 'English'}.
            This was their one nudge.
          </p>
        </div>
      ) : disabledReason ? (
        <p className="text-sm text-zinc-600">{disabledReason}</p>
      ) : (
        <>
          <p className="text-sm text-zinc-600 mb-3">
            Sends ONE stage-specific recovery email in the user's language, from Kurt.
            The server recomputes their stage live, checks the suppression list, and
            permanently records the send. There is no second nudge.
          </p>
          {confirming ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={sending}
                onClick={send}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#0d1f14] text-[#b5f03d] hover:opacity-90 disabled:opacity-50"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
                {sending ? 'Sending…' : 'Yes, send their one nudge'}
              </button>
              <button
                type="button"
                disabled={sending}
                onClick={() => setConfirming(false)}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-zinc-300 text-zinc-700 hover:bg-zinc-50"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border border-zinc-300 text-zinc-800 hover:bg-zinc-50"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
              Send nudge email…
            </button>
          )}
        </>
      )}

      {error && (
        <p className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>
      )}
    </div>
  );
}
